import { InternalAccessor } from '@billy/design-system'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  productUnitCategoryToTranslationKey,
  productUnitModalCategories,
} from '../../../constants/productUnitCategories'
import { ProductUnitCategory } from '../../../enums/productUnitCategory'
import type { ProductUnit } from '../../../types/productUnit'
import { getProductUnitFullName } from '../../../utils/getProductUnitFullName'
import { getProductUnitName } from '../../../utils/getProductUnitName'
import { groupProductUnitsByCategory } from '../../../utils/groupProductUnitsByCategory'
import type { ProductUnitTableItem } from '../types/productUnitTableItem'

interface UseProductUnitsModalOptions {
  baseUnits: ProductUnit[]
}

export const useProductUnitsModal = ({ baseUnits }: UseProductUnitsModalOptions) => {
  const [units, setUnits] = useState<ProductUnit[]>(baseUnits)
  const [unitToDelete, setUnitToDelete] = useState<string | undefined>()
  const [lastUnitAdded, setLastUnitAdded] = useState<string | undefined>()
  const [unitToAdd, setUnitToAdd] = useState('')

  const { t } = useTranslation()

  const normalizedUnitToAdd = unitToAdd.trim()

  const isUnitDuplicated = useMemo(() => {
    const unitToAddLowerCase = normalizedUnitToAdd.toLowerCase()

    return units.some((unit) => {
      const fullName = getProductUnitFullName(unit, t)
      // Some full names list alternatives, e.g. "Piece / Unit"
      const fullNameParts = fullName ? [fullName, ...fullName.split('/')] : []

      return [getProductUnitName(unit, t), ...fullNameParts].some(
        (name) => name.trim().toLowerCase() === unitToAddLowerCase,
      )
    })
  }, [normalizedUnitToAdd, t, units])

  const isAddUnitDisabled = !normalizedUnitToAdd || isUnitDuplicated

  const tableUnits = useMemo<ProductUnitTableItem[]>(
    () =>
      groupProductUnitsByCategory(units, productUnitModalCategories).flatMap(({ category, units: categoryUnits }) => [
        {
          id: `category-${category}`,
          unit: t(productUnitCategoryToTranslationKey[category]),
          [InternalAccessor.HoverActions]: false,
          [InternalAccessor.SectionHeader]: true,
        },
        ...categoryUnits.map((unit) => ({
          fullName: getProductUnitFullName(unit, t),
          id: unit.label ?? unit.name ?? '',
          // Displayed translated; `units` (saved back as-is) keeps the raw, backend-stable `name`
          unit: getProductUnitName(unit, t),
          [InternalAccessor.HoverActions]: category === ProductUnitCategory.Custom,
        })),
      ]),
    [t, units],
  )

  const handleUnitInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setUnitToAdd(value)
  }, [])

  const handleAddUnit = useCallback(() => {
    if (isAddUnitDisabled) {
      return
    }

    setUnits((units) => [...units, { category: ProductUnitCategory.Custom, name: normalizedUnitToAdd }])
    setLastUnitAdded(normalizedUnitToAdd)
    setUnitToAdd('')
  }, [isAddUnitDisabled, normalizedUnitToAdd])

  return {
    isAddUnitDisabled,
    isUnitDuplicated,
    lastUnitAdded,
    tableUnits,
    unitToAdd,
    unitToDelete,
    units,
    handleAddUnit,
    handleUnitInputChange,
    setUnitToDelete,
    setUnits,
  }
}
